import { GRAPHICS_QUALITY_ORDER, GraphicsProfile, GraphicsRemoteState, ShadowMode, isGraphicsQuality } from "./graphics_quality.js";

export interface GraphicsRemoteConfig extends GraphicsRemoteState
{
  version: number;
}

export const EMPTY_GRAPHICS_CONFIG: GraphicsRemoteConfig = { version: 1, default_quality: null, force_quality: null, tiers: {} };

const SHADOW_MODES: ShadowMode[] = [ "off", "basic", "pcf", "pcf_soft" ];
const SHADOW_MAP_SIZES = [ 256, 512, 1024, 2048, 4096 ];

function asRecord( value: unknown ): Record<string, unknown>
{
  return value && typeof value === "object" && !Array.isArray( value )
    ? value as Record<string, unknown>
    : {};
}

function numberIn( value: unknown, min: number, max: number ): number | undefined
{
  const n = typeof value === "number" ? value : Number( value );
  return Number.isFinite( n ) && n >= min && n <= max ? n : undefined;
}

function boolean( value: unknown ): boolean | undefined
{
  return typeof value === "boolean" ? value : undefined;
}

type FieldReader = ( value: unknown ) => unknown;

const FIELD_READERS: Record<keyof GraphicsProfile, FieldReader> = {
  msaa: boolean,
  shadows: value => typeof value === "string" && SHADOW_MODES.includes( value as ShadowMode ) ? value : undefined,
  shadow_map_size: value => SHADOW_MAP_SIZES.includes( Number( value ) ) ? Number( value ) : undefined,
  shadow_refresh_divisor: value => { const n = numberIn( value, 1, 6 ); return n === undefined ? undefined : Math.floor( n ); },
  receive_shadows: boolean,
  rim_light: boolean,
  hemisphere_intensity: value => numberIn( value, 0, 2 ),
  normal_maps: boolean,
  refraction: boolean,
  ball_shadow: boolean,
  sky_animation: boolean,
  specular: boolean,
  max_pixel_ratio: value => numberIn( value, 0, 4 ),
  render_scale: value => numberIn( value, 0.3, 1 ),
  ui_render_scale: value => numberIn( value, 0.3, 1 ),
  ui_effects: boolean,
};

export function normalizeTierOverride( raw: unknown ): Partial<GraphicsProfile>
{
  const source = asRecord( raw );
  const result: Record<string, unknown> = {};
  for ( const key of Object.keys( FIELD_READERS ) as ( keyof GraphicsProfile )[] )
  {
    if ( !( key in source ) ) continue;
    const value = FIELD_READERS[key]( source[key] );
    if ( value !== undefined )
      result[key] = value;
  }
  return result as Partial<GraphicsProfile>;
}

export function normalizeGraphicsConfig( raw: unknown ): GraphicsRemoteConfig
{
  const value = asRecord( raw );
  const tiers_raw = asRecord( value.tiers );
  const tiers: GraphicsRemoteConfig["tiers"] = {};
  for ( const quality of GRAPHICS_QUALITY_ORDER )
  {
    if ( !( quality in tiers_raw ) ) continue;
    const tier = normalizeTierOverride( tiers_raw[quality] );
    if ( Object.keys( tier ).length > 0 )
      tiers[quality] = tier;
  }
  const version = numberIn( value.version, 1, 1_000_000 );
  return {
    version: version === undefined ? 1 : Math.floor( version ),
    default_quality: isGraphicsQuality( value.default_quality ) ? value.default_quality : null,
    force_quality: isGraphicsQuality( value.force_quality ) ? value.force_quality : null,
    tiers,
  };
}

